// src/components/QuizQuestion.tsx
import React, { useEffect } from 'react';
import { QuizQuestion as QQ } from '../data/quizQuestions';

interface QuizQuestionProps {
  question: QQ;
  index: number;
  total: number;
  onAnswer: (answerIndex: number) => void;
  onBack: () => void;
  canBack: boolean;
}

const QuizQuestion: React.FC<QuizQuestionProps> = ({ question, index, total, onAnswer, onBack, canBack }) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= question.answers.length) {
        onAnswer(n - 1);
        return;
      }
      if (e.key === 'Escape' || e.key === 'ArrowLeft') {
        if (canBack) onBack();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [question, onAnswer, onBack, canBack]);

  const progress = ((index + 1) / total) * 100;

  return (
    <article className="max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between font-mono text-xs text-steel uppercase tracking-[0.15em] mb-3">
          <span>Question {index + 1} of {total}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-1 bg-grid-line">
          <div className="h-full bg-command-amber transition-all duration-200" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <h2 className="text-3xl md:text-4xl font-bold text-signal-white mb-12 leading-tight">
        {question.question}
      </h2>

      <ul className="space-y-3">
        {question.answers.map((a, i) => (
          <li key={i}>
            <button
              onClick={() => onAnswer(i)}
              className="w-full p-5 border border-grid-line text-left flex items-start gap-4 group hover:border-command-amber transition-colors"
            >
              <span className="font-mono text-xs text-steel group-hover:text-command-amber transition-colors pt-1">{i + 1}</span>
              <span className="text-signal-white">{a.label}</span>
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-8 flex items-center justify-between gap-6">
        <button
          onClick={onBack}
          disabled={!canBack}
          className="-ml-3 px-3 py-2 min-h-[44px] font-mono text-xs uppercase tracking-[0.15em] text-steel hover:text-signal-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          ← Back
        </button>
        {/* A keyboard hint is dead weight on a phone, and below md it
            collided with Back on one line. */}
        <span className="hidden md:inline font-mono text-xs text-steel uppercase tracking-[0.15em]">
          Press 1-{question.answers.length} or click an answer
        </span>
      </div>
    </article>
  );
};

export default QuizQuestion;
